import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ProtectedRoute, RoleProtectedRoute } from "./ProtectedRoute";
import Login from "../pages/Login";
import Dashboard from "../pages/Dashboard";
import AdminOverview from "../pages/AdminOverview";
import ShiftsTable from "../pages/ShiftsTable";
import ActiveShift from "../pages/ActiveShift";
import ArchiveShift from "../pages/ArchiveShift";
import AvailabilityTable from "../pages/AvailabilityTable";
import AvailabilityCards from "../pages/AvailabilityCards";
import TimesheetTable from "../pages/TimesheetTable";
import InvoiceTable from "../pages/InvoiceTable";
import InvoiceShift from "../pages/InvoiceShift";
import UsersTable from "../pages/UsersTable";
import UserDetail from "../pages/UserDetail";
import UserMessages from "../pages/UserMessages";
import MyMessages from "../pages/MyMessages";
import MessageCreation from "../pages/MessageCreation";
import ShiftCreation from "../pages/ShiftCreation";
import EmailTable from "../pages/EmailTable";
import LiveShifts from "../pages/LiveShifts";
import DisplayFacilities from "../pages/DisplayFacilities";
import { StatisticsDashboard } from "../pages/StatisticsDashboard";
import { CertificatesManagement } from "../pages/CertificatesManagement";
import { Reporting } from "../pages/Reporting";
import { HelloWorld } from "../pages/HelloWorld";
import ReferencesTable from "../pages/ReferencesTable";

const Routes = () => {
  const { token } = useAuth();

  // Define public routes accessible to all users
  const routesForPublic = [
    {
      path: "/hello-world",
      element: <HelloWorld />,
    },
  ];

  // Define routes accessible only to authenticated users
  const routesForAuthenticatedOnly = [
    {
      path: "/",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <AdminOverview />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/live",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <LiveShifts />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/shifts",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <ShiftsTable />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/shifts/new",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <ShiftCreation />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/shifts/view/:shiftId",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <ActiveShift />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/archiveShifts/view/:shiftId",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <ArchiveShift />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/timesheets",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <TimesheetTable />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/invoices",
      element: (
        <RoleProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
          <Dashboard>
            <InvoiceTable />
          </Dashboard>
        </RoleProtectedRoute>
      ),
    },
    {
      path: "/invoice/:invoiceId",
      element: (
        <RoleProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
          <Dashboard>
            <InvoiceShift />
          </Dashboard>
        </RoleProtectedRoute>
      ),
    },
    {
      path: "/users",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <UsersTable />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/users/:userId",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <UserDetail />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/references",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <ReferencesTable />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/statistics",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <StatisticsDashboard />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/nurse-certificates",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <CertificatesManagement />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/availability",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <AvailabilityTable />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/availabilityCards",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <AvailabilityCards />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/reports",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <Reporting />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/reports/timesheeting",
      element: (
        <ProtectedRoute>
          <Dashboard></Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/reports/expired_certificates",
      element: (
        <ProtectedRoute>
          <Dashboard></Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/messages",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <UserMessages />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/my_messages",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <MyMessages />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/messages/new",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <MessageCreation />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/emails",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <EmailTable />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
    {
      path: "/facilities",
      element: (
        <ProtectedRoute>
          <Dashboard>
            <DisplayFacilities />
          </Dashboard>
        </ProtectedRoute>
      ),
    },
  ];

  // Define routes accessible only to non-authenticated users
  const routesForNotAuthenticatedOnly = [
    {
      path: "/login",
      element: <Login />,
    },
  ];

  // Combine and conditionally include routes based on authentication status
  const router = createBrowserRouter([
    ...routesForPublic,
    ...(!token ? routesForNotAuthenticatedOnly : []),
    ...routesForAuthenticatedOnly,
  ]);

  // Provide the router configuration using RouterProvider
  return <RouterProvider router={router} />;
};

export default Routes;
